import { supabase } from '../lib/supabase';

export const alertService = {
  // ============================================================================
  // ALERT MANAGEMENT
  // ============================================================================

  async getAllAlerts() {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      alertType: row?.alert_type,
      severity: row?.severity,
      title: row?.title,
      message: row?.message,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      affectedCount: row?.affected_count,
      deadline: row?.deadline,
      alertStatus: row?.alert_status,
      readAt: row?.read_at,
      dismissedAt: row?.dismissed_at,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getUnreadAlerts() {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('*')
      ?.eq('alert_status', 'unread')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      alertType: row?.alert_type,
      severity: row?.severity,
      title: row?.title,
      message: row?.message,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      affectedCount: row?.affected_count,
      deadline: row?.deadline,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async getActiveAlerts() {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('*')
      ?.in('alert_status', ['unread', 'read'])
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      alertType: row?.alert_type,
      severity: row?.severity,
      title: row?.title,
      message: row?.message,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      affectedCount: row?.affected_count,
      deadline: row?.deadline,
      alertStatus: row?.alert_status,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async getAlertsByType(alertType) {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('*')
      ?.eq('alert_type', alertType)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      severity: row?.severity,
      title: row?.title,
      message: row?.message,
      alertStatus: row?.alert_status,
      createdAt: row?.created_at
    })) || [];
  },

  async markAsRead(alertId) {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.update({
        alert_status: 'read',
        read_at: new Date()?.toISOString()
      })
      ?.eq('id', alertId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      alertStatus: data?.alert_status,
      readAt: data?.read_at
    };
  },

  async dismissAlert(alertId) {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.update({
        alert_status: 'dismissed',
        dismissed_at: new Date()?.toISOString()
      })
      ?.eq('id', alertId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      alertStatus: data?.alert_status,
      dismissedAt: data?.dismissed_at
    };
  },

  async markAllAsRead() {
    const { error } = await supabase
      ?.from('system_alerts')
      ?.update({
        alert_status: 'read',
        read_at: new Date()?.toISOString()
      })
      ?.eq('alert_status', 'unread');
    
    if (error) throw error;
  },

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToAlerts(callback) {
    const channel = supabase
      ?.channel('system_alerts_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'system_alerts'
        },
        (payload) => {
          const newAlert = {
            id: payload?.new?.id,
            alertType: payload?.new?.alert_type,
            severity: payload?.new?.severity,
            title: payload?.new?.title,
            message: payload?.new?.message,
            entityType: payload?.new?.entity_type,
            entityId: payload?.new?.entity_id,
            affectedCount: payload?.new?.affected_count,
            deadline: payload?.new?.deadline,
            alertStatus: payload?.new?.alert_status,
            metadata: payload?.new?.metadata,
            createdAt: payload?.new?.created_at
          };
          callback(newAlert);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromAlerts(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getAlertStats() {
    const { data, error } = await supabase
      ?.from('system_alerts')
      ?.select('alert_type, severity, alert_status');
    
    if (error) throw error;
    
    const stats = {
      total: data?.length || 0,
      unread: data?.filter(a => a?.alert_status === 'unread')?.length || 0,
      critical: data?.filter(a => a?.severity === 'critical')?.length || 0,
      byType: {
        nasfundDeadline: data?.filter(a => a?.alert_type === 'nasfund_deadline')?.length || 0,
        swtFilingError: data?.filter(a => a?.alert_type === 'swt_filing_error')?.length || 0,
        complianceViolation: data?.filter(a => a?.alert_type === 'compliance_violation')?.length || 0,
        payrollApprovalStatus: data?.filter(a => a?.alert_type === 'payroll_approval_status')?.length || 0
      }
    };
    
    return stats;
  }
};

export default alertService;